import React, { useEffect, useState } from 'react';
import { X, Sparkles, Send, User, ArrowRight, ChevronRight, PlusCircle, Shield } from 'lucide-react';
type MessageType = 'user' | 'ai';
interface Message {
  type: MessageType;
  content: string | React.ReactNode;
  hasSuggestions?: boolean;
}
interface SuggestionProps {
  text: string;
  onClick: () => void;
}
export const AIChat = ({
  onClose,
  toggleRiskPanel,
  sendAutoMessage = false
}: {
  onClose: () => void;
  toggleRiskPanel: () => void;
  sendAutoMessage?: boolean;
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const initialSuggestions = ["What's the member risk profile like?", 'What was the last transaction made by the user?', 'When did they pay their last credit card bill?'];
  // Effect to send automatic message when opened from Risk Panel
  useEffect(() => {
    if (sendAutoMessage) {
      const riskProfileCard = <div className="bg-white border rounded-lg p-3 mb-3" data-id="element-13">
          <div className="flex items-center gap-2 mb-2" data-id="element-14">
            <Shield size={18} className="text-purple-500" data-id="element-15" />
            <div className="font-medium" data-id="element-16">Sarah's Risk Profile</div>
          </div>
          <div className="text-sm text-gray-600 mb-2" data-id="element-17">
            <div className="flex justify-between mb-1" data-id="element-18">
              <span data-id="element-19">Risk Score:</span>
              <span className="text-red-600 font-medium" data-id="element-20">75 (High)</span>
            </div>
            <div className="flex justify-between mb-1" data-id="element-21">
              <span data-id="element-22">Device Anomalies:</span>
              <span data-id="element-23">Detected</span>
            </div>
            <div className="flex justify-between mb-1" data-id="element-24">
              <span data-id="element-25">Geographic Anomalies:</span>
              <span data-id="element-26">790 miles</span>
            </div>
            <div className="flex justify-between" data-id="element-27">
              <span data-id="element-28">Last Login:</span>
              <span data-id="element-29">3:24 AM (Unusual)</span>
            </div>
          </div>
        </div>;
      // Add user message with risk profile card
      setMessages([{
        type: 'user',
        content: <div data-id="element-30">
              {riskProfileCard}
              <div data-id="element-31">Analyze this</div>
            </div>
      }]);
      // Simulate AI response
      setTimeout(() => {
        handleRiskProfileAnalysis();
      }, 800);
    }
  }, [sendAutoMessage]);
  const resetChat = () => {
    setMessages([]);
    setInputValue('');
  };
  const handleViewRiskProfile = () => {
    onClose(); // Close the AI Chat first
    toggleRiskPanel(); // Then open the Risk Panel
  };
  const handleRiskProfileAnalysis = () => {
    const response: Message = {
      type: 'ai',
      content: <div data-id="element-32">
          <p className="font-medium mb-3" data-id="element-33">
            Risk Profile Analysis for Sarah Johnson
          </p>
          <div className="mb-3" data-id="element-34">
            <p className="text-red-600 font-medium mb-1" data-id="element-35">High Risk Score: 75</p>
            <p className="mb-2" data-id="element-36">
              Sarah's risk score of 75 places her in the high-risk category,
              significantly above the industry average of 42 for financial
              services customers. This score indicates potential security
              concerns that require immediate attention.
            </p>
          </div>
          <div className="mb-3" data-id="element-37">
            <p className="font-medium mb-1" data-id="element-38">Key Risk Factors:</p>
            <ol className="list-decimal pl-5 space-y-1.5" data-id="element-39">
              <li data-id="element-40">
                <span className="font-medium" data-id="element-41">Device Anomalies:</span> A new
                device (iPhone Safari) was detected in Chicago, IL, which
                differs from Sarah's usual Windows/Chrome setup. This device
                change combined with a location change represents a significant
                risk indicator.
              </li>
              <li data-id="element-42">
                <span className="font-medium" data-id="element-43">Unusual Login Timing:</span> Login
                activity at 3:24 AM falls well outside Sarah's normal usage
                patterns (typically 8 AM - 6 PM), suggesting potential account
                compromise.
              </li>
              <li data-id="element-44">
                <span className="font-medium" data-id="element-45">Geographic Anomaly:</span> Login
                location in Chicago is approximately 790 miles from Sarah's
                usual location in New York. While physically possible to travel
                this distance, the combination with other factors elevates the
                risk.
              </li>
              <li data-id="element-46">
                <span className="font-medium" data-id="element-47">
                  Multiple Password Reset Attempts:
                </span>{' '}
                Three password reset attempts within 15 minutes prior to the
                unusual login suggest a potential brute force attempt.
              </li>
            </ol>
          </div>
          <div className="mb-3" data-id="element-48">
            <p className="font-medium mb-1" data-id="element-49">Risk Assessment:</p>
            <p data-id="element-50">
              The clustering of multiple risk factors within a short timeframe
              (geographic change, device change, unusual hour, and password
              resets) creates a high-probability account compromise scenario.
              This pattern matches known attack vectors where credentials are
              obtained and then used from a different location to avoid
              immediate detection.
            </p>
          </div>
          <div data-id="element-51">
            <p className="font-medium mb-1" data-id="element-52">Recommended Actions:</p>
            <ul className="list-disc pl-5 space-y-1" data-id="element-53">
              <li data-id="element-54">
                Implement immediate step-up authentication for any financial
                transactions
              </li>
              <li data-id="element-55">
                Contact Sarah through verified channels to confirm recent login
                activity
              </li>
              <li data-id="element-56">Enable two-factor authentication (currently disabled)</li>
              <li data-id="element-57">
                Temporarily restrict high-value transactions until identity is
                confirmed
              </li>
              <li data-id="element-58">
                Review recent transaction history for any unauthorized activity
              </li>
            </ul>
          </div>
        </div>,
      hasSuggestions: true
    };
    setMessages(prev => [...prev, response]);
  };
  const handleSendMessage = (text: string) => {
    // Add user message
    setMessages(prev => [...prev, {
      type: 'user',
      content: text
    }]);
    // Process the message and generate a response
    setTimeout(() => {
      let response: Message = {
        type: 'ai',
        content: '',
        hasSuggestions: true
      };
      // Main response logic
      if (text.toLowerCase().includes('risk profile')) {
        response.content = <div data-id="element-59">
            <p className="mb-3" data-id="element-60">
              Here's a summary of Sarah Johnson's risk profile:
            </p>
            <ul className="list-disc pl-5 mb-3 space-y-1" data-id="element-61">
              <li data-id="element-62">
                Current risk score:{' '}
                <span className="text-red-600 font-medium" data-id="element-63">75 (High)</span>
              </li>
              <li data-id="element-64">Device anomalies detected in the last 24 hours</li>
              <li data-id="element-65">Unusual login time at 3:24 AM</li>
              <li data-id="element-66">
                Geographic anomaly: Login from Chicago (790 miles from usual
                location)
              </li>
              <li data-id="element-67">Multiple password reset attempts detected</li>
            </ul>
            <button onClick={handleViewRiskProfile} className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium flex items-center" data-id="element-68">
              View Detailed Risk Profile
              <ChevronRight size={16} className="ml-1" data-id="element-69" />
            </button>
          </div>;
      } else if (text.toLowerCase().includes('improve the risk score')) {
        response.content = <div data-id="element-70">
            <p className="mb-3" data-id="element-71">
              To improve Sarah Johnson's risk score, consider these actions:
            </p>
            <ul className="list-disc pl-5 mb-3 space-y-1" data-id="element-72">
              <li data-id="element-73">Enable two-factor authentication (currently disabled)</li>
              <li data-id="element-74">Verify the recent device login from Chicago</li>
              <li data-id="element-75">Schedule a security review call with the customer</li>
              <li data-id="element-76">Suggest password reset with a stronger password</li>
              <li data-id="element-77">Review and confirm recent transactions</li>
            </ul>
            <p className="text-sm text-gray-600" data-id="element-78">
              Taking these actions could reduce the risk score by approximately
              15-20 points.
            </p>
          </div>;
      } else if (text.toLowerCase().includes('triggered the risk alerts')) {
        response.content = <div data-id="element-79">
            <p className="mb-3" data-id="element-80">
              The following events triggered risk alerts for Sarah Johnson:
            </p>
            <div className="space-y-3" data-id="element-81">
              <div className="bg-red-50 p-3 rounded-md border border-red-100" data-id="element-82">
                <div className="font-medium text-red-800" data-id="element-83">
                  High Severity Triggers
                </div>
                <ul className="list-disc pl-5 mt-1 text-sm text-red-700" data-id="element-84">
                  <li data-id="element-85">
                    Login from a new device (iPhone) in Chicago at 10:15 AM
                  </li>
                  <li data-id="element-86">Unusual login time at 3:24 AM (outside normal hours)</li>
                  <li data-id="element-87">Geographic anomaly: 790 miles from usual location</li>
                </ul>
              </div>
              <div className="bg-amber-50 p-3 rounded-md border border-amber-100" data-id="element-88">
                <div className="font-medium text-amber-800" data-id="element-89">
                  Medium Severity Triggers
                </div>
                <ul className="list-disc pl-5 mt-1 text-sm text-amber-700" data-id="element-90">
                  <li data-id="element-91">
                    Multiple password reset attempts (3 within 15 minutes)
                  </li>
                  <li data-id="element-92">VPN usage detected during login</li>
                </ul>
              </div>
            </div>
          </div>;
      } else if (text.toLowerCase().includes('industry average')) {
        response.content = <div data-id="element-93">
            <p className="mb-3" data-id="element-94">
              Comparing Sarah Johnson's risk profile with industry averages:
            </p>
            <div className="bg-white border rounded-lg p-3 mb-3" data-id="element-95">
              <div className="flex justify-between items-center mb-2" data-id="element-96">
                <div className="font-medium" data-id="element-97">Sarah's Risk Score</div>
                <div className="text-red-600 font-medium" data-id="element-98">75</div>
              </div>
              <div className="flex justify-between items-center mb-2" data-id="element-99">
                <div className="font-medium" data-id="element-100">Financial Services Average</div>
                <div className="text-amber-600 font-medium" data-id="element-101">42</div>
              </div>
              <div className="flex justify-between items-center" data-id="element-102">
                <div className="font-medium" data-id="element-103">Similar Customer Profiles</div>
                <div className="text-green-600 font-medium" data-id="element-104">38</div>
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-105">
              Sarah's risk score is{' '}
              <span className="font-medium text-red-600" data-id="element-106">78% higher</span> than
              the industry average for financial services customers.
            </p>
          </div>;
      } else if (text.toLowerCase().includes('analyze this') && text.toLowerCase().includes('risk profile')) {
        // This would be triggered by our auto-message, but we handle it separately with handleRiskProfileAnalysis
        return;
      } else if (text.toLowerCase().includes('last transaction')) {
        response.content = <div data-id="element-107">
            <p className="mb-2" data-id="element-108">
              The last transaction made by Sarah Johnson was:
            </p>
            <div className="bg-gray-50 p-3 rounded-md border mb-2" data-id="element-109">
              <div className="font-medium" data-id="element-110">Coffee Shop Purchase</div>
              <div className="text-sm text-gray-600" data-id="element-111">$4.75 at Starbucks</div>
              <div className="text-sm text-gray-600" data-id="element-112">Today, 8:45 AM</div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-113">
                Debit Card ending in 4832
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-114">
              No unusual activity detected with this transaction.
            </p>
          </div>;
      } else if (text.toLowerCase().includes('transactions today')) {
        response.content = <div data-id="element-115">
            <p className="mb-2" data-id="element-116">
              Here are all transactions for Sarah Johnson today:
            </p>
            <div className="space-y-2 mb-3" data-id="element-117">
              <div className="bg-gray-50 p-3 rounded-md border" data-id="element-118">
                <div className="font-medium" data-id="element-119">Coffee Shop Purchase</div>
                <div className="text-sm text-gray-600" data-id="element-120">$4.75 at Starbucks</div>
                <div className="text-sm text-gray-600" data-id="element-121">Today, 8:45 AM</div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-122">
                  Debit Card ending in 4832
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md border" data-id="element-123">
                <div className="font-medium" data-id="element-124">Grocery Store</div>
                <div className="text-sm text-gray-600" data-id="element-125">
                  $37.22 at Whole Foods
                </div>
                <div className="text-sm text-gray-600" data-id="element-126">Today, 11:20 AM</div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-127">
                  Credit Card ending in 5678
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md border" data-id="element-128">
                <div className="font-medium" data-id="element-129">Pharmacy</div>
                <div className="text-sm text-gray-600" data-id="element-130">$12.99 at CVS</div>
                <div className="text-sm text-gray-600" data-id="element-131">Today, 2:15 PM</div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-132">
                  Debit Card ending in 4832
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-133">
              Total spending today: $54.96
            </p>
          </div>;
      } else if (text.toLowerCase().includes('suspicious transactions')) {
        response.content = <div data-id="element-134">
            <p className="mb-3" data-id="element-135">
              I found 1 potentially suspicious transaction in the last 7 days:
            </p>
            <div className="bg-amber-50 p-3 rounded-md border border-amber-200 mb-3" data-id="element-136">
              <div className="font-medium text-amber-800" data-id="element-137">
                Online Electronics Purchase
              </div>
              <div className="text-sm text-amber-700" data-id="element-138">
                $899.00 at TechGadgetsOnline
              </div>
              <div className="text-sm text-amber-700" data-id="element-139">
                November 23, 2023, 2:34 AM
              </div>
              <div className="text-xs text-amber-600 mt-1" data-id="element-140">
                Credit Card ending in 5678
              </div>
              <div className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded mt-2 inline-block" data-id="element-141">
                Flagged: Unusual merchant & time
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-142">
              This transaction was flagged because it occurred at an unusual
              time and with a merchant Sarah hasn't used before.
            </p>
          </div>;
      } else if (text.toLowerCase().includes('spending pattern')) {
        response.content = <div data-id="element-143">
            <p className="mb-3" data-id="element-144">Sarah Johnson's spending pattern analysis:</p>
            <div className="bg-white border rounded-lg p-3 mb-3" data-id="element-145">
              <div className="font-medium mb-2" data-id="element-146">
                Top Spending Categories (Last 30 Days)
              </div>
              <div className="space-y-2" data-id="element-147">
                <div data-id="element-148">
                  <div className="flex justify-between text-sm" data-id="element-149">
                    <span data-id="element-150">Groceries</span>
                    <span data-id="element-151">$342.18</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full mt-1" data-id="element-152">
                    <div className="w-[35%] h-full bg-blue-500 rounded-full" data-id="element-153"></div>
                  </div>
                </div>
                <div data-id="element-154">
                  <div className="flex justify-between text-sm" data-id="element-155">
                    <span data-id="element-156">Dining</span>
                    <span data-id="element-157">$286.45</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full mt-1" data-id="element-158">
                    <div className="w-[28%] h-full bg-purple-500 rounded-full" data-id="element-159"></div>
                  </div>
                </div>
                <div data-id="element-160">
                  <div className="flex justify-between text-sm" data-id="element-161">
                    <span data-id="element-162">Shopping</span>
                    <span data-id="element-163">$210.32</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full mt-1" data-id="element-164">
                    <div className="w-[22%] h-full bg-green-500 rounded-full" data-id="element-165"></div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-166">
              Sarah's spending is consistent with her historical patterns. No
              anomalies detected in category distribution.
            </p>
          </div>;
      } else if (text.toLowerCase().includes('credit card bill')) {
        response.content = <div data-id="element-167">
            <p className="mb-2" data-id="element-168">Sarah Johnson's last credit card payment:</p>
            <div className="bg-gray-50 p-3 rounded-md border mb-2" data-id="element-169">
              <div className="font-medium" data-id="element-170">Credit Card Payment</div>
              <div className="text-sm text-gray-600" data-id="element-171">$1,250.00</div>
              <div className="text-sm text-gray-600" data-id="element-172">November 15, 2023</div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-173">
                From Checking Account ending in 7890
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-174">
              Next payment due: December 15, 2023
            </p>
            <p className="text-sm text-gray-600" data-id="element-175">Current balance: $745.28</p>
          </div>;
      } else if (text.toLowerCase().includes('credit limit')) {
        response.content = <div data-id="element-176">
            <p className="mb-3" data-id="element-177">Sarah Johnson's credit card details:</p>
            <div className="bg-white border rounded-lg p-3 mb-3" data-id="element-178">
              <div className="grid grid-cols-2 gap-3" data-id="element-179">
                <div data-id="element-180">
                  <div className="text-sm text-gray-500" data-id="element-181">Credit Limit</div>
                  <div className="font-medium" data-id="element-182">$5,000.00</div>
                </div>
                <div data-id="element-183">
                  <div className="text-sm text-gray-500" data-id="element-184">Available Credit</div>
                  <div className="font-medium" data-id="element-185">$4,254.72</div>
                </div>
                <div data-id="element-186">
                  <div className="text-sm text-gray-500" data-id="element-187">Current Balance</div>
                  <div className="font-medium" data-id="element-188">$745.28</div>
                </div>
                <div data-id="element-189">
                  <div className="text-sm text-gray-500" data-id="element-190">Utilization</div>
                  <div className="font-medium" data-id="element-191">14.9%</div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-192">
              Sarah's credit utilization is well below the recommended 30%
              threshold.
            </p>
          </div>;
      } else if (text.toLowerCase().includes('payment schedule')) {
        response.content = <div data-id="element-193">
            <p className="mb-3" data-id="element-194">
              Sarah Johnson's credit card payment schedule:
            </p>
            <div className="bg-white border rounded-lg p-3 mb-3" data-id="element-195">
              <div className="space-y-3" data-id="element-196">
                <div className="flex justify-between items-center pb-2 border-b" data-id="element-197">
                  <div data-id="element-198">
                    <div className="font-medium" data-id="element-199">Next Payment Due</div>
                    <div className="text-sm text-gray-600" data-id="element-200">Minimum: $25.00</div>
                  </div>
                  <div className="text-right" data-id="element-201">
                    <div className="font-medium" data-id="element-202">December 15, 2023</div>
                    <div className="text-sm text-gray-600" data-id="element-203">
                      21 days from now
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-2 border-b" data-id="element-204">
                  <div data-id="element-205">
                    <div className="font-medium" data-id="element-206">Last Payment</div>
                    <div className="text-sm text-gray-600" data-id="element-207">
                      Full balance paid
                    </div>
                  </div>
                  <div className="text-right" data-id="element-208">
                    <div className="font-medium" data-id="element-209">$1,250.00</div>
                    <div className="text-sm text-gray-600" data-id="element-210">
                      November 15, 2023
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-211">
                  <div data-id="element-212">
                    <div className="font-medium" data-id="element-213">Statement Closing Date</div>
                  </div>
                  <div className="text-right" data-id="element-214">
                    <div className="font-medium" data-id="element-215">December 3, 2023</div>
                    <div className="text-sm text-gray-600" data-id="element-216">9 days from now</div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      } else if (text.toLowerCase().includes('autopay')) {
        response.content = <div data-id="element-217">
            <p className="mb-3" data-id="element-218">Sarah Johnson's autopay status:</p>
            <div className="bg-white border rounded-lg p-3 mb-3" data-id="element-219">
              <div className="flex items-center justify-between" data-id="element-220">
                <div data-id="element-221">
                  <div className="font-medium" data-id="element-222">Autopay Status</div>
                  <div className="text-sm text-gray-600" data-id="element-223">
                    For credit card ending in 5678
                  </div>
                </div>
                <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium" data-id="element-224">
                  Enabled
                </div>
              </div>
              <div className="mt-3 pt-3 border-t" data-id="element-225">
                <div className="grid grid-cols-2 gap-3" data-id="element-226">
                  <div data-id="element-227">
                    <div className="text-sm text-gray-500" data-id="element-228">Payment Amount</div>
                    <div className="font-medium" data-id="element-229">Minimum payment</div>
                  </div>
                  <div data-id="element-230">
                    <div className="text-sm text-gray-500" data-id="element-231">Payment Source</div>
                    <div className="font-medium" data-id="element-232">Checking ****7890</div>
                  </div>
                  <div data-id="element-233">
                    <div className="text-sm text-gray-500" data-id="element-234">Payment Date</div>
                    <div className="font-medium" data-id="element-235">Due date</div>
                  </div>
                  <div data-id="element-236">
                    <div className="text-sm text-gray-500" data-id="element-237">Last Updated</div>
                    <div className="font-medium" data-id="element-238">Aug 12, 2023</div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600" data-id="element-239">
              Note: Sarah is only paying the minimum amount automatically.
              Consider suggesting full balance payments to avoid interest
              charges.
            </p>
          </div>;
      } else {
        response.content = <div data-id="element-240">
            <p data-id="element-241">
              I don't have specific information about that query. Here are some
              things I can help you with:
            </p>
            <ul className="list-disc pl-5 mt-2" data-id="element-242">
              <li data-id="element-243">Customer account information</li>
              <li data-id="element-244">Recent transactions</li>
              <li data-id="element-245">Risk profile details</li>
              <li data-id="element-246">Payment history</li>
            </ul>
          </div>;
      }
      setMessages(prev => [...prev, response]);
    }, 500);
  };
  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };
  const getFollowupSuggestions = (messageIndex: number): string[] => {
    const message = messages[messageIndex];
    if (message.type === 'ai') {
      if (message.content && typeof message.content === 'object') {
        const contentStr = JSON.stringify(message.content);
        // Special follow-ups for the risk profile analysis
        if (contentStr.includes('Risk Profile Analysis for Sarah Johnson')) {
          return ['What specific actions should we take?', 'How does this compare to industry average?', 'What caused the geographic anomaly?', 'Show suspicious transactions'];
        } else if (contentStr.includes('risk profile')) {
          return ['How can I improve the risk score?', 'What triggered the risk alerts?', 'Compare with industry average'];
        } else if (contentStr.includes('last transaction')) {
          return ['Show all transactions today', 'Any suspicious transactions?', "What's the spending pattern?"];
        } else if (contentStr.includes('credit card')) {
          return ["What's the credit limit?", 'Show payment schedule', 'Is autopay enabled?'];
        }
      }
      return ['Tell me more about the customer', 'Show recent account activity', "What's the contact history?"];
    }
    return [];
  };
  return <div className="w-[400px] bg-white border-l border-r flex flex-col h-full shadow-lg" data-id="element-247">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 py-6 flex items-center justify-between" data-id="element-248">
        <div className="flex items-center" data-id="element-249">
          <Sparkles size={20} data-id="element-250" />
          <span className="font-medium pl-2" data-id="element-251">AI Assistant</span>
        </div>
        <div className="flex items-center space-x-4" data-id="element-252">
          <button onClick={resetChat} className="flex items-center text-white hover:text-blue-100" title="New Chat" data-id="element-253">
            <PlusCircle size={18} data-id="element-254" />
          </button>
          <button onClick={() => onClose(false)} data-id="element-255">
            <X size={20} data-id="element-256" />
          </button>
        </div>
      </div>
      {/* Chat Messages */}
      <div className="flex-1 p-4 overflow-y-auto" data-id="element-257">
        {messages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center p-6" data-id="element-258">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-259">
              <Sparkles size={24} className="text-blue-500" data-id="element-260" />
            </div>
            <h3 className="text-lg font-medium mb-2" data-id="element-261">
              How can I help you today?
            </h3>
            <p className="text-gray-500 mb-6" data-id="element-262">
              Ask me anything about Sarah's account or select a suggestion
              below.
            </p>
            <div className="w-full space-y-2" data-id="element-263">
              {initialSuggestions.map((suggestion, index) => <Suggestion key={index} text={suggestion} onClick={() => handleSuggestionClick(suggestion)} data-id="element-264" />)}
            </div>
          </div> : <div className="space-y-6" data-id="element-265">
            {messages.map((message, index) => <div key={index} data-id="element-266">
                <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-267">
                  <div className="flex items-start max-w-[80%]" data-id="element-268">
                    {message.type === 'ai' && <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white mr-2 flex-shrink-0" data-id="element-269">
                        <Sparkles size={16} data-id="element-270" />
                      </div>}
                    <div className={`p-3 rounded-lg ${message.type === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-800'}`} data-id="element-271">
                      {message.content}
                    </div>
                    {message.type === 'user' && <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center ml-2 flex-shrink-0" data-id="element-272">
                        <User size={16} className="text-gray-500" data-id="element-273" />
                      </div>}
                  </div>
                </div>
                {/* Follow-up suggestions after AI messages */}
                {message.type === 'ai' && message.hasSuggestions && <div className="mt-3 ml-10" data-id="element-274">
                    <div className="text-xs text-gray-500 mb-2" data-id="element-275">
                      Suggested follow-ups:
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-276">
                      {getFollowupSuggestions(index).map((suggestion, suggIndex) => <button key={suggIndex} onClick={() => handleSuggestionClick(suggestion)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full" data-id="element-277">
                            {suggestion}
                          </button>)}
                    </div>
                  </div>}
              </div>)}
          </div>}
      </div>
      {/* Input Area */}
      <div className="border-t p-4" data-id="element-278">
        <div className="flex items-center border rounded-lg bg-white overflow-hidden" data-id="element-279">
          <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={e => {
          if (e.key === 'Enter' && inputValue.trim()) {
            handleSendMessage(inputValue);
            setInputValue('');
          }
        }} placeholder="Type your message..." className="flex-1 p-3 outline-none" data-id="element-280" />
          <button onClick={() => {
          if (inputValue.trim()) {
            handleSendMessage(inputValue);
            setInputValue('');
          }
        }} className="p-3 text-blue-500" data-id="element-281">
            <Send size={20} data-id="element-282" />
          </button>
        </div>
        <div className="flex items-center mt-2 text-xs text-gray-500" data-id="element-283">
          <Sparkles size={12} className="mr-1" data-id="element-284" />
          <span data-id="element-285">Powered by AI Assistant</span>
        </div>
      </div>
    </div>;
};
const Suggestion = ({
  text,
  onClick
}: SuggestionProps) => {
  return <button onClick={onClick} className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-between" data-id="element-286">
      <span data-id="element-287">{text}</span>
      <ArrowRight size={16} className="text-gray-400" data-id="element-288" />
    </button>;
};